import { Checkbox, InputAdornment, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useParams } from "react-router";
import { DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "../../configurator-store";
import { getAttributeDisplayName, getSelectedAttributeIds } from "../../configurator-utils";
import { VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID, VIEW_SETTINGS_PRODUCT_LABELS_ITEM_ID, VIEW_SETTINGS_PRODUCT_TYPE_ITEM_ID, viewSettingsActions } from "./view-settings-store";

const mapStateToProps = (state: RootState) => ({
  applied: state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied,
  draft: state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.draft,
  attributesCatalog: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.attributesCatalog
});

const ownActions = {
  setDraftSelectedItemIds: viewSettingsActions.setDraftSelectedItemIds,
  applyDraft: viewSettingsActions.applyDraft,
  clearAll: viewSettingsActions.clearAll,
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

// Which "Not selected" section an item belongs to. Only that list is sectioned — "Selected" stays a
// single flat, drag-orderable list because its order is the user's chip-order preference.
type TPickableItemKind = "GENERAL" | "ATTRIBUTE";

type TPickableItem = { id: string; name: string; kind: TPickableItemKind };

// Fixed section order for the "Not selected" list; items inside each are sorted alphabetically
const NOT_SELECTED_SECTION_ORDER: TPickableItemKind[] = ["GENERAL", "ATTRIBUTE"];

const DRAG_TYPE = "VIEW_SETTINGS_SELECTED_ITEM_ROW";
type TDragItem = { id: string };

// Reorders by id rather than raw index — the rendered "Selected" list can be narrowed by search,
// so a visual drag position doesn't always match its index in the underlying (unfiltered) order.
const DraggableSelectedItemRow = ({ id, name, moveItem, onToggle }: { id: string; name: string; moveItem: (fromId: string, toId: string) => void; onToggle: (id: string) => void }) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<TDragItem>({
    accept: DRAG_TYPE,
    hover(item) {
      if (!ref.current || item.id === id) return;
      moveItem(item.id, id);
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: (): TDragItem => ({ id }),
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  drag(drop(ref));

  return (
    <Stack ref={ref} direction="row" alignItems="center" gap={0.5} sx={{ opacity: isDragging ? 0.4 : 1, cursor: "move" }} onClick={() => onToggle(id)}>
      <DekoxIcon icon="moreVertical" fontSize="small" sx={{ color: theme.palette.customColors.darkGrey }} />
      <Checkbox checked size="small" onClick={e => e.stopPropagation()} onChange={() => onToggle(id)} />
      <Typography variant="body2">{name}</Typography>
    </Stack>
  );
};

const ViewSettingsBase = (props: Props) => {
  const { applied, draft, attributesCatalog, setDraftSelectedItemIds, applyDraft, clearAll, setActiveOptionsPanel, getConfigurationsRequest } = props;
  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();
  const [search, setSearch] = useState("");
  const { projectId = "" } = useParams<{ projectId: string }>();

  // attributesCatalog is a Loading<T> union (same source Filter itself reads) — only "has-loaded" carries the attribute list
  const attributes = attributesCatalog.loading === "has-loaded" ? attributesCatalog.data : [];

  // The entity-label tags group is just another pickable, reorderable item — no separate toggle.
  const pickableItems: TPickableItem[] = useMemo(
    () => [
      { id: VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID, name: configuratorPageT.configurationLabels, kind: "GENERAL" },
      { id: VIEW_SETTINGS_PRODUCT_LABELS_ITEM_ID, name: configuratorPageT.productLabels, kind: "GENERAL" },
      { id: VIEW_SETTINGS_PRODUCT_TYPE_ITEM_ID, name: configuratorPageT.productType, kind: "GENERAL" },
      ...attributes.map((a): TPickableItem => ({ id: a.id, name: getAttributeDisplayName(a), kind: "ATTRIBUTE" }))
    ],
    [attributes, configuratorPageT.configurationLabels, configuratorPageT.productLabels, configuratorPageT.productType]
  );

  const sectionTitleByKind: Record<TPickableItemKind, string> = {
    GENERAL: configuratorPageT.general,
    ATTRIBUTE: configuratorPageT.attributes
  };
  const itemsById = useMemo(() => new Map(pickableItems.map(item => [item.id, item])), [pickableItems]);

  // Seed the draft from the applied selection whenever the panel opens — otherwise a user who opens
  // the panel, makes no changes, and closes it would see a stale draft from a prior open silently applied.
  useEffect(() => {
    setDraftSelectedItemIds(applied.selectedItemIds);
  }, []);

  const filteredItems = useMemo(() => pickableItems.filter(item => item.name.toLowerCase().includes(search.toLowerCase())), [pickableItems, search]);

  // Selected list follows draft.selectedItemIds' order (the user's own drag order), not filteredItems' order.
  const selectedItems = useMemo(
    () => draft.selectedItemIds.map(id => itemsById.get(id)).filter((item): item is TPickableItem => !!item && item.name.toLowerCase().includes(search.toLowerCase())),
    [draft.selectedItemIds, itemsById, search]
  );
  // Grouped into General / Attributes, each alphabetical. Empty sections are dropped so
  // a search that matches only attributes doesn't leave a bare heading behind.
  const notSelectedSections = useMemo(() => {
    const selectedIds = new Set(draft.selectedItemIds);
    const remaining = filteredItems.filter(item => !selectedIds.has(item.id));
    return NOT_SELECTED_SECTION_ORDER.map(kind => ({
      kind,
      items: remaining.filter(item => item.kind === kind).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    })).filter(section => section.items.length > 0);
  }, [filteredItems, draft.selectedItemIds]);

  const toggleItem = (itemId: string) => {
    const isSelected = draft.selectedItemIds.includes(itemId);
    setDraftSelectedItemIds(isSelected ? draft.selectedItemIds.filter(id => id !== itemId) : [...draft.selectedItemIds, itemId]);
  };

  const moveSelectedItem = (fromId: string, toId: string) => {
    const ids = [...draft.selectedItemIds];
    const fromIndex = ids.indexOf(fromId);
    const toIndex = ids.indexOf(toId);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, moved);
    setDraftSelectedItemIds(ids);
  };

  const handleApply = () => {
    const appliedAttributeIds = getSelectedAttributeIds(applied.selectedItemIds);
    const draftAttributeIds = getSelectedAttributeIds(draft.selectedItemIds);
    const attributeSelectionChanged = appliedAttributeIds.length !== draftAttributeIds.length || draftAttributeIds.some(id => !appliedAttributeIds.includes(id));

    applyDraft();
    if (attributeSelectionChanged) {
      getConfigurationsRequest(projectId);
    }
  };

  return (
    <Stack overflow="hidden" width="100%" py={2} flex={1} minHeight={0}>
      {/* Header */}
      <Stack flexShrink={0} px={3} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {configuratorPageT.viewSettings}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Stack gap={1} direction="row" alignItems="center">
            <TooltipShortcut shortcut={{ action: handleApply, shortCutKey: "a", modifier: "alt" }}>
              <Button color="success" t={genericTranslationsT} k={genericTranslationsK} field="apply" variant="outlined" size="small" onClick={handleApply} />
            </TooltipShortcut>
            <TooltipShortcut shortcut={{ action: clearAll, shortCutKey: "n", modifier: "alt" }}>
              <Button color="error" t={genericTranslationsT} k={genericTranslationsK} field="reset" variant="text" size="small" onClick={clearAll} />
            </TooltipShortcut>
          </Stack>
          <TooltipShortcut shortcut={{ action: () => setActiveOptionsPanel(null), shortCutKey: "v", modifier: "alt" }}>
            <IconButton k={genericTranslationsK.close} size="small" onClick={() => setActiveOptionsPanel(null)}>
              <DekoxIcon icon="doubleArrowRight" fontSize="small" />
            </IconButton>
          </TooltipShortcut>
        </Stack>
      </Stack>
      {/* Content */}
      <Stack overflow="auto" flex={1} minHeight={0} gap={2} mt={2} px={3} py={2}>
        <TextField
          size="small"
          placeholder={genericTranslationsT.search}
          value={search}
          onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <DekoxIcon icon="search" fontSize="small" />
                </InputAdornment>
              )
            }
          }}
        />

        {selectedItems.length > 0 && (
          <Stack gap={0.5}>
            <Typography variant="caption" color="text.secondary">
              {configuratorPageT.selected}
            </Typography>
            {selectedItems.map(item => (
              <DraggableSelectedItemRow key={item.id} id={item.id} name={item.name} moveItem={moveSelectedItem} onToggle={toggleItem} />
            ))}
          </Stack>
        )}

        {notSelectedSections.length > 0 && (
          <Stack gap={0.5}>
            {/* The caption only earns its place as a contrast to "Selected" — with an empty selection the
                whole panel is unselected items, so it would be labelling everything. */}
            {selectedItems.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {configuratorPageT.notSelected}
              </Typography>
            )}
            {notSelectedSections.map(section => (
              <Stack key={section.kind} gap={0.5} data-itk-test="viewSettingsNotSelectedSection">
                <Typography variant="caption" color="text.secondary" fontWeight={600} mt={1}>
                  {sectionTitleByKind[section.kind]}
                </Typography>
                {section.items.map(item => (
                  <Stack key={item.id} direction="row" alignItems="center" sx={{ cursor: "pointer" }} onClick={() => toggleItem(item.id)}>
                    <Checkbox checked={false} size="small" onClick={e => e.stopPropagation()} onChange={() => toggleItem(item.id)} />
                    <Typography variant="body2">{item.name}</Typography>
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export const ViewSettings = connect(mapStateToProps, ownActions)(ViewSettingsBase);
